import * as RadixTabs from '@radix-ui/react-tabs';
import clsx from 'clsx';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Icon } from '../display/Icon.tsx';
import styles from './Tabs.module.css';

export interface TabItem<V extends string> {
  readonly value: V;
  readonly label: string;
  readonly icon?: LucideIcon | undefined;
  /** Small count next to the label, e.g. unread messages. */
  readonly count?: number | undefined;
}

export interface TabsProps<V extends string> {
  readonly value: V;
  readonly onValueChange: (value: V) => void;
  readonly children: ReactNode;
  readonly className?: string | undefined;
}

export const Tabs = <V extends string>({ value, onValueChange, children, className }: TabsProps<V>) => (
  <RadixTabs.Root
    className={clsx(styles.root, className)}
    value={value}
    onValueChange={(next) => onValueChange(next as V)}
  >
    {children}
  </RadixTabs.Root>
);

export interface TabListProps<V extends string> {
  readonly label: string;
  readonly items: readonly TabItem<V>[];
  readonly className?: string | undefined;
}

export const TabList = <V extends string>({ label, items, className }: TabListProps<V>) => (
  <RadixTabs.List aria-label={label} className={clsx(styles.list, className)}>
    {items.map((item) => (
      <RadixTabs.Trigger key={item.value} value={item.value} className={styles.trigger}>
        {item.icon && <Icon icon={item.icon} size="sm" />}
        <span className={styles.label}>{item.label}</span>
        {item.count !== undefined && item.count > 0 && <span className={styles.count}>{item.count}</span>}
      </RadixTabs.Trigger>
    ))}
  </RadixTabs.List>
);

export interface TabPanelProps {
  readonly value: string;
  readonly children: ReactNode;
  readonly className?: string | undefined;
  /** Keep the panel mounted while hidden (preserves scroll position and media). */
  readonly keepMounted?: boolean | undefined;
}

export const TabPanel = ({ value, children, className, keepMounted }: TabPanelProps) => (
  <RadixTabs.Content
    value={value}
    className={clsx(styles.panel, className)}
    {...(keepMounted ? { forceMount: true } : {})}
  >
    {children}
  </RadixTabs.Content>
);
